import React from 'react';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import * as validators from '../../util/validators';
import { propTypes } from '../../util/types';

import { Button, FieldSelect, FieldTextInput, Form } from '..';

import css from './MidtransAccountForm.module.css';

const CreateMidtransAccountFields = props => {
  const { disabled, values, intl, banks, loadBanksInProgress, loadBanksError } = props;

  const banksLabel = intl.formatMessage({
    id: 'MidtransAccountForm.banks',
  });

  const showFields = !!values.bank && !loadBanksInProgress && !loadBanksError;

  return (
    <div className={css.sectionContainer}>
      <div>
        {loadBanksInProgress ? (
          <div>
            <label>{banksLabel}</label>
            <div>
              <FormattedMessage id="MidtransAccountForm.loadingBanks" />
            </div>
          </div>
        ) : (
          <FieldSelect
            id="bank"
            name="bank"
            disabled={disabled}
            className={css.formRow}
            autoComplete="bank"
            label={banksLabel}
            validate={validators.required(
              intl.formatMessage({
                id: 'MidtransAccountForm.bankRequired',
              })
            )}
          >
            <option disabled value="">
              {intl.formatMessage({ id: 'MidtransAccountForm.bankPlaceholder' })}
            </option>
            {banks.map(b => {
              const bank = b.code || b.bank_code || b.id || b.name;
              const bankName = b.name || b.bank_name || b.code;
              return (
                <option key={bank} value={bank}>
                  {bankName}
                </option>
              );
            })}
          </FieldSelect>
        )}
        {showFields ? (
          <div>
            <FieldTextInput
              className={css.formRow}
              type="text"
              id="accountNumber"
              name="accountNumber"
              label={intl.formatMessage({ id: 'MidtransAccountForm.accountNumber' })}
              validate={validators.required(
                intl.formatMessage({
                  id: 'MidtransAccountForm.accountNumberRequired',
                })
              )}
            />
            <FieldTextInput
              className={css.formRow}
              type="text"
              id="accountHolderName"
              name="accountHolderName"
              label={intl.formatMessage({ id: 'MidtransAccountForm.accountHolderName' })}
              validate={validators.required(
                intl.formatMessage({
                  id: 'MidtransAccountForm.accountHolderNameRequired',
                })
              )}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};

const ErrorsMaybe = props => {
  const { payoutDetailsSaveError } = props;
  const isNameMismatchError = payoutDetailsSaveError === 'Account name does not match bank record';
  const errorMessage = isNameMismatchError ? (
    <FormattedMessage
      id="MidtransAccountForm.nameMismatchError"
      values={{ message: payoutDetailsSaveError }}
    />
  ) : payoutDetailsSaveError ? (
    <FormattedMessage
      id="MidtransAccountForm.errorSavingPayoutDetails"
      values={{ message: payoutDetailsSaveError }}
    />
  ) : null;

  return errorMessage ? <div className={css.error}>{errorMessage}</div> : null;
};

/**
 * A component that renders a midtrans payout form.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {function} props.onSubmit - The function to call when the form is submitted
 * @param {propTypes.currentUser} props.currentUser - The current user
 * @param {boolean} props.disabled - Whether the form is disabled
 * @param {boolean} props.inProgress - Whether the form is in progress
 * @param {boolean} props.ready - Whether the form is ready
 * @param {string} props.submitButtonText - The text for the submit button
 * @param {Object} props.fieldRenderProps - The field render props
 * @returns {JSX.Element}
 */
const MidtransAccountForm = props => {
  const intl = useIntl();
  const { onSubmit, ...restOfProps } = props;

  return (
    <FinalForm
      {...restOfProps}
      onSubmit={onSubmit}
      mutators={{
        ...arrayMutators,
      }}
      render={fieldRenderProps => {
        const {
          rootClassName,
          className,
          children,
          disabled,
          handleSubmit,
          inProgress,
          invalid,
          pristine,
          ready,
          submitButtonText,
          form: formApi,
          values,
          currentUser,
          authScopes,
          banks,
          loadBanksInProgress,
          loadBanksError,
          payoutDetailsSaveError,
        } = fieldRenderProps;

        // Check if current user has limited rights in order to disable the submit button
        const limitedRights = authScopes?.indexOf('user:limited') >= 0;
        const submitInProgress = inProgress;
        const submitDisabled = pristine || invalid || disabled || submitInProgress || limitedRights;

        const classes = classNames(rootClassName || css.root, className, {
          [css.disabled]: disabled,
        });

        const isLoading = loadBanksInProgress;
        const midtransAccountFields = isLoading ? (
          <div className={css.savedInformation}>
            <FormattedMessage id="MidtransAccountForm.loadingMidtranseAccountData" />
          </div>
        ) : (
          <CreateMidtransAccountFields
            disabled={disabled}
            values={values}
            intl={intl}
            banks={banks}
            loadBanksInProgress={loadBanksInProgress}
            loadBanksError={loadBanksError}
          />
        );

        // Add button title for hovering to explain why the button is disabled when user has limited rights
        const submitButtonTitle = limitedRights
          ? intl.formatMessage({ id: 'MidtransAccountForm.buttonTitleLimitedAccess' })
          : null;

        const submitButton = (
          <Button
            type="submit"
            inProgress={submitInProgress}
            disabled={submitDisabled}
            ready={ready}
            title={submitButtonTitle}
          >
            {submitButtonText || <FormattedMessage id="MidtransAccountForm.submitButtonText" />}
          </Button>
        );

        return (
          <Form className={classes} onSubmit={handleSubmit}>
            {midtransAccountFields}
            <ErrorsMaybe payoutDetailsSaveError={payoutDetailsSaveError} />
            {children}
            {submitButton}
          </Form>
        );
      }}
    />
  );
};

export default MidtransAccountForm;
